import { Card, Section } from '@factorialco/deprecated-design-system'
import {
  CreateOpenApplicationSettingDocument,
  GetOpenApplicationSettingsDocument,
  ToggleEnabledOpenApplicationSettingDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import OpenApplicationForm from './OpenApplicationForm'

const OpenApplicationSettings = () => {
  const { company } = useCurrentSession()
  const { data, loading } = useQuery(GetOpenApplicationSettingsDocument, {
    variables: { companyId: company.id },
  })

  const [create, { loading: creating }] = useMutation(CreateOpenApplicationSettingDocument, {
    extractorFn: (data) => data.atsMutations.createAtsOpenApplicationSetting,
    refetchQueries: [GetOpenApplicationSettingsDocument],
  })

  const [toggleEnabledAtsOpenApplicationSetting, { loading: updating }] = useMutation(
    ToggleEnabledOpenApplicationSettingDocument,
    {
      extractorFn: (data) => data.atsMutations.toggleEnabledAtsOpenApplicationSetting,
    }
  )

  const openApplicationSetting = data?.ats.openApplicationSettingsConnection.edges[0]?.node
  const hasOpenApplicationSetting = !loading && openApplicationSetting

  const onCreate = async () => {
    const result = await create({
      variables: {
        companyId: company.id,
        enabled: true,
        cvRequirement: 'optional',
        phoneRequirement: 'optional',
        photoRequirement: 'optional',
        personalUrlRequirement: 'optional',
        genderRequirement: 'do_not_ask',
      },
    })

    const handled = handleResult(result, (_errors) => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
  }

  const onToggle = async () => {
    const result = await toggleEnabledAtsOpenApplicationSetting({
      variables: {
        id: openApplicationSetting!.id,
        enabled: !openApplicationSetting!.enabled,
      },
    })

    const handled = handleResult(result, (_errors) => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }

    if (openApplicationSetting?.enabled) {
      track(trackingEvents.ats.disableOpenApplications)
    } else {
      track(trackingEvents.ats.enableOpenApplications)
    }
  }

  return (
    <Section.TwoThirds
      description={i18n.t('ats.settings.candidate_data.open_application_settings.description')}
      illustration="people-add"
      title={i18n.t('ats.settings.candidate_data.open_application_settings.title')}
    >
      {!loading && (
        <Card>
          <Card.Title size="small">
            {i18n.t('ats.settings.candidate_data.open_application_settings.toggle.title')}
          </Card.Title>
          <Card.Description>
            {i18n.t('ats.settings.candidate_data.open_application_settings.toggle.description')}
          </Card.Description>
          <Card.ExpandableContent
            expanded={openApplicationSetting?.enabled ?? false}
            disabled={loading || creating || updating}
            onExpandChange={hasOpenApplicationSetting ? onToggle : onCreate}
          >
            {openApplicationSetting && (
              <OpenApplicationForm openApplicationSetting={openApplicationSetting} />
            )}
          </Card.ExpandableContent>
        </Card>
      )}
    </Section.TwoThirds>
  )
}

export default OpenApplicationSettings
